import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import HeroSection from './components/HeroSection';
import { categories, articles } from '@/mocks/articles';

export function useNewsletterForm() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setStatus('success');
      setMessage('感谢订阅！');
      setEmail('');
      return;
    }
    setStatus('loading');
    setMessage('');
    try {
      const resp = await fetch('https://readdy.ai/api/form/d9sldod8o1vn02ekuhb0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          email: formData.get('email') as string,
        }).toString(),
      });
      const text = await resp.text();
      let parsed: any = {};
      try { parsed = JSON.parse(text); } catch {}
      const code = parsed?.code;
      const serverMsg = parsed?.meta?.message || parsed?.message || '';
      if (resp.ok && code === 'OK') {
        setStatus('success');
        setMessage('感谢订阅！我们将第一时间为您推送孟若羽的最新动态。');
        setEmail('');
      } else {
        setStatus('error');
        setMessage(serverMsg || '订阅失败，请稍后重试。');
      }
    } catch {
      // form submission failed, handled below
      setStatus('error');
      setMessage('网络错误，请稍后重试。');
    }
  }, []);

  return { email, setEmail, status, message, handleSubmit };
}

export default function Home() {
  const latestArticles = articles.slice(0, 6);
  const { email, setEmail, status, message, handleSubmit } = useNewsletterForm();

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        <HeroSection />

        <section className="w-full px-6 md:px-10 py-6 bg-background-100">
          <div className="flex flex-wrap items-center justify-center gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex items-center gap-2 px-4 py-2 bg-background-50 rounded-full text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-200 whitespace-nowrap"
              >
                <i className={`${cat.icon} text-base`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </section>

        <section className="w-full px-6 md:px-10 py-16 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-10 gap-4">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                  最新动态
                </h2>
                <p className="text-foreground-600 text-sm">
                  第一时间了解孟若羽的最新资讯与精彩瞬间
                </p>
              </div>
              <Link
                to="/category/news"
                className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestArticles.map((article) => (
                <ArticleCard
                  key={article.slug}
                  slug={article.slug}
                  title={article.title}
                  excerpt={article.excerpt}
                  imageUrl={article.imageUrl}
                  date={article.date}
                  readTime={article.readTime}
                  categoryName={article.categoryName}
                  tags={article.tags}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-6 md:px-10 py-16 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-10 gap-4">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                  作品集锦
                </h2>
                <p className="text-foreground-600 text-sm">
                  汇集孟若羽所有精彩影视作品与综艺节目
                </p>
              </div>
              <Link
                to="/category/works"
                className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <Link to="/category/works" className="group relative rounded-lg overflow-hidden bg-background-50 aspect-[3/4]">
                <img src="https://readdy.ai/api/search-image?query=Elegant%20Chinese%20actress%20in%20modern%20urban%20drama%20role%2C%20cinematic%20portrait%2C%20warm%20indoor%20lighting%2C%20film%20poster%20aesthetic%2C%20cream%20and%20gold%20tones%2C%20confident%20expression%2C%20professional%20photography&width=600&height=800&seq=home-work-01&orientation=portrait" alt="春风十里" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <span className="inline-block px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-medium rounded mb-2">电视剧</span>
                  <h3 className="font-heading text-lg font-semibold text-background-50 mb-1">春风十里</h3>
                  <p className="text-background-50/70 text-sm">饰演：林小满 · 2025</p>
                </div>
              </Link>

              <Link to="/category/works" className="group relative rounded-lg overflow-hidden bg-background-50 aspect-[3/4]">
                <img src="https://readdy.ai/api/search-image?query=Elegant%20Chinese%20actress%20in%20realistic%20film%20role%2C%20cinematic%20portrait%2C%20soft%20natural%20lighting%2C%20arthouse%20movie%20poster%20aesthetic%2C%20warm%20earthy%20tones%2C%20introspective%20expression%2C%20high%20quality%20photography&width=600&height=800&seq=home-work-02&orientation=portrait" alt="城市之光" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <span className="inline-block px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-medium rounded mb-2">电影</span>
                  <h3 className="font-heading text-lg font-semibold text-background-50 mb-1">城市之光</h3>
                  <p className="text-background-50/70 text-sm">饰演：苏念 · 2025</p>
                </div>
              </Link>

              <Link to="/category/works" className="group relative rounded-lg overflow-hidden bg-background-50 aspect-[3/4]">
                <img src="https://readdy.ai/api/search-image?query=Elegant%20Chinese%20actress%20on%20stage%20theater%20performance%2C%20dramatic%20spotlight%20portrait%2C%20live%20stage%20aesthetic%2C%20warm%20theater%20lighting%2C%20emotional%20intense%20expression%2C%20artistic%20photography&width=600&height=800&seq=home-work-03&orientation=portrait" alt="繁花" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <span className="inline-block px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-medium rounded mb-2">舞台剧</span>
                  <h3 className="font-heading text-lg font-semibold text-background-50 mb-1">繁花</h3>
                  <p className="text-background-50/70 text-sm">饰演：李小姐 · 2025</p>
                </div>
              </Link>

              <Link to="/category/works" className="group relative rounded-lg overflow-hidden bg-background-50 aspect-[3/4]">
                <img src="https://readdy.ai/api/search-image?query=Elegant%20Chinese%20actress%20in%20ancient%20Chinese%20costume%20drama%2C%20cinematic%20historical%20portrait%2C%20soft%20golden%20lantern%20light%2C%20traditional%20hanfu%20silk%20fabric%2C%20classical%20beauty%20aesthetic%2C%20high%20quality%20photography&width=600&height=800&seq=home-work-04&orientation=portrait" alt="长安赋" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <span className="inline-block px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-medium rounded mb-2">电视剧</span>
                  <h3 className="font-heading text-lg font-semibold text-background-50 mb-1">长安赋</h3>
                  <p className="text-background-50/70 text-sm">饰演：沈清辞 · 2026</p>
                </div>
              </Link>
            </div>
          </div>
        </section>

        <section className="w-full px-6 md:px-10 py-16 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                粉丝互动专区
              </h2>
              <p className="text-foreground-600 text-sm max-w-lg mx-auto">
                加入孟若羽粉丝大家庭，获取独家内容与互动福利
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-background-100 rounded-lg p-8 text-center group hover:bg-background-200 transition-colors duration-300">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-heart-pulse-line text-2xl"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">
                  粉丝社群
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  加入官方粉丝群，与全国各地的"羽毛"们一起交流互动，结交志同道合的朋友。
                </p>
              </div>

              <div className="bg-background-100 rounded-lg p-8 text-center group hover:bg-background-200 transition-colors duration-300">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-gift-line text-2xl"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">
                  独家福利
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  抢先获取孟若羽签名照、周边礼品，参与线下见面会专属抽奖。
                </p>
              </div>

              <div className="bg-background-100 rounded-lg p-8 text-center group hover:bg-background-200 transition-colors duration-300">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                  <i className="ri-message-3-line text-2xl"></i>
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">
                  在线互动
                </h3>
                <p className="text-foreground-600 text-sm leading-relaxed">
                  参与线上问答、投票活动，有机会被孟若羽亲自翻牌回复哦！
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-6 md:px-10 py-16 bg-primary-500">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-14 h-14 mx-auto mb-5 flex items-center justify-center rounded-full bg-background-50/20 text-background-50">
              <i className="ri-mail-send-line text-2xl"></i>
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">
              订阅最新资讯
            </h2>
            <p className="text-background-50/80 text-sm mb-8">
              第一时间获取孟若羽的最新动态、作品发布和粉丝福利信息
            </p>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="flex flex-col sm:flex-row items-center gap-3 max-w-md mx-auto"
            >
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="visually-hidden"
              />
              <input
                type="email"
                name="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="请输入您的邮箱地址"
                className="w-full sm:flex-1 px-5 py-3 rounded-full bg-background-50 text-foreground-950 placeholder:text-foreground-400 text-sm outline-none"
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full sm:w-auto px-8 py-3 bg-foreground-950 text-background-50 font-medium rounded-full hover:bg-foreground-900 transition-colors duration-200 whitespace-nowrap disabled:opacity-50"
              >
                {status === 'loading' ? '提交中...' : '立即订阅'}
              </button>
            </form>

            {message && (
              <p
                className={`mt-4 text-sm ${
                  status === 'success' ? 'text-background-50' : 'text-background-50/90'
                }`}
              >
                {message}
              </p>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}